import { Form, Formik, FormikConfig, useField, useFormikContext } from 'formik';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';
import { Button } from '../../components/Button';
import { FormCheckBox } from '../../components/formik/FormCheckBox';
import { FormikInput } from '../../components/formik/FormInput';
import { FormikSelect } from '../../components/formik/FormSelect';
import { Option } from '../../components/inputs/Select';
import { useReferralPayoutCadenceTranslations, useReferralRoleTranslations } from '../../hooks/translations/useReferralRoleTranslations';
import { ToastType, useToastNotifications } from '../../hooks/useToastNotifications';
import { useValidationTranslations } from '../../hooks/useValidationTranslations';
import { T, useT } from '../../translation/src';
import { ReferralPayoutCadence, ReferralRole, SuperadminOrganisationFragment, useSuperadminSetReferralSettingsMutation } from '../../types/graphqlTypes';

// From the least to the most: a customer earns projects, a lead generator a commission on the customers it brings
// in, a reseller orders for its customers (at a discount), a full-service partner also supports them.
const ROLES_IN_ORDER = [ReferralRole.Customer, ReferralRole.LeadGenerator, ReferralRole.Reseller, ReferralRole.FullService] as const;

// A role's cuts unless agreed otherwise, in percent (the server's DEFAULT_CUTS_BY_ROLE).
const DEFAULT_CUTS: Record<ReferralRole, { first: number; recurring: number }> = {
  [ReferralRole.Customer]: { first: 0, recurring: 0 },
  [ReferralRole.LeadGenerator]: { first: 20, recurring: 0 },
  [ReferralRole.Reseller]: { first: 20, recurring: 10 },
  [ReferralRole.FullService]: { first: 40, recurring: 40 },
};

interface FormValues {
  role: ReferralRole;
  // In percent here; the API speaks basis points. On the customer's opening subscription, and on everything after.
  firstCutPercent: number;
  recurringCutPercent: number;
  customersCanCancel: boolean;
  payoutCadence: ReferralPayoutCadence;
  // In euros here; the API speaks cents.
  minPayoutEuros: number;
}

export interface ReferralSettingsPanelProps {
  organisationId: string;
  referral: SuperadminOrganisationFragment['referral'];
}
// A new role brings its default cuts into the fields (they stay editable: an agreement may differ).
const RoleDefaults: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const previous = useRef(values.role);
  useEffect(() => {
    if (previous.current === values.role) return;
    previous.current = values.role;
    void setFieldValue('firstCutPercent', DEFAULT_CUTS[values.role].first);
    void setFieldValue('recurringCutPercent', DEFAULT_CUTS[values.role].recurring);
  }, [setFieldValue, values.role]);
  return null;
};

// What the role uses, and nothing else: a customer earns projects (no settings); a lead generator a commission on
// the first subscription, paid out on a cadence above a minimum; a reseller or full-service partner a discount on
// the first order and after, taken off its invoices (no payouts), and it decides whether its customers may
// cancel. What is hidden keeps its value.
const RoleFields: React.FC = () => {
  const { values } = useFormikContext<FormValues>();
  const cadences = useReferralPayoutCadenceTranslations();
  const commissionLabel = useT('commission on the first subscription (%)', { swc: true });
  const firstDiscountLabel = useT('discount on first subscription (%)', { swc: true });
  const recurringDiscountLabel = useT('discount on future purchases and renewals (%)', { swc: true });
  const cadenceLabel = useT('payout', { swc: true });
  const minimumLabel = useT('minimum payout (€)', { swc: true });
  const [canCancel] = useField<boolean>({ name: 'customersCanCancel', type: 'checkbox' });

  if (values.role === ReferralRole.Customer)
    return (
      <span className="text-xs text-gray-500">
        <T _str="A customer earns a free project for every paid subscription of the organisations it refers, and bonus projects at 5, 10 and 20. Nothing to set." />
      </span>
    );

  if (values.role === ReferralRole.LeadGenerator)
    return (
      <>
        <FormikInput name="firstCutPercent" type="number" step="0.01" min={0} max={100} label={commissionLabel} />
        <span className="text-xs text-gray-500">
          <T _str="Only on the customer's opening subscription. Pythagoras invoices the customer. The commission is paid out by vendor bill." />
        </span>
        <FormikSelect name="payoutCadence" label={cadenceLabel}>
          {Object.values(ReferralPayoutCadence).map((cadence) => (
            <Option key={cadence} value={cadence}>
              {cadences[cadence]}
            </Option>
          ))}
        </FormikSelect>
        <FormikInput name="minPayoutEuros" type="number" step="1" min={0} label={minimumLabel} />
        <span className="text-xs text-gray-500">
          <T _str="Below the minimum, the period's payout waits for the next one. Paying out by hand ignores it." />
        </span>
      </>
    );

  return (
    <>
      <div className="flex gap-3">
        <FormikInput name="firstCutPercent" type="number" step="0.01" min={0} max={100} label={firstDiscountLabel} />
        <FormikInput name="recurringCutPercent" type="number" step="0.01" min={0} max={100} label={recurringDiscountLabel} />
      </div>
      <span className="text-xs text-gray-500">
        <T _str="Renewals, upgrades and extra projects are future purchases. The discount is taken off the partner's invoices. There are no payouts." />
      </span>
      <FormCheckBox name={canCancel.name} checked={!!canCancel.checked} onChange={canCancel.onChange} onBlur={canCancel.onBlur} label={<T _str="Allow end customers to cancel their subscriptions" />} />
    </>
  );
};

// What the organisation is to the referral programme, its cuts and how often it is paid out. Earlier sales
// keep the role and cut they were attributed with.
const ReferralSettingsPanel_: React.FC<ReferralSettingsPanelProps> = ({ organisationId, referral }) => {
  // A new role changes the relations of the linked customers on the server: the list shows them anew.
  const [setSettings, { loading }] = useSuperadminSetReferralSettingsMutation({ refetchQueries: ['superadminOrganisations'] });
  const { showToast } = useToastNotifications();
  const validation = useValidationTranslations();
  const roles = useReferralRoleTranslations();
  const failed = useT('could not save the referral settings', { swc: true });
  const saved = useT('referral settings saved', { swc: true });
  const roleLabel = useT('partner role', { swc: true });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        role: yup.mixed<ReferralRole>().oneOf(Object.values(ReferralRole)).required(validation.isRequired),
        firstCutPercent: yup.number().min(0).max(100).required(validation.isRequired),
        recurringCutPercent: yup.number().min(0).max(100).required(validation.isRequired),
        payoutCadence: yup.mixed<ReferralPayoutCadence>().oneOf(Object.values(ReferralPayoutCadence)).required(validation.isRequired),
        minPayoutEuros: yup.number().min(0).required(validation.isRequired),
      }),
    [validation.isRequired],
  );

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values) => {
      try {
        await setSettings({
          variables: {
            organisationId,
            input: {
              role: values.role,
              firstCutBasisPoints: Math.round(Number(values.firstCutPercent) * 100),
              recurringCutBasisPoints: Math.round(Number(values.recurringCutPercent) * 100),
              payoutCadence: values.payoutCadence,
              minPayoutCents: Math.round(Number(values.minPayoutEuros) * 100),
              customersCanCancel: values.customersCanCancel,
            },
          },
        });
        showToast({ title: saved, text: '', type: ToastType.SUCCESS });
      } catch (e) {
        showToast({ title: failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
      }
    },
    [failed, organisationId, saved, setSettings, showToast],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: {
        role: referral.role,
        firstCutPercent: referral.firstCutBasisPoints / 100,
        recurringCutPercent: referral.recurringCutBasisPoints / 100,
        customersCanCancel: referral.customersCanCancel,
        payoutCadence: referral.payoutCadence,
        minPayoutEuros: referral.minPayoutCents / 100,
      },
      onSubmit,
      validationSchema,
      enableReinitialize: true,
    }),
    [onSubmit, referral, validationSchema],
  );

  return (
    <Formik<FormValues> {...formik}>
      <Form className="flex flex-col max-w-md gap-2">
        <FormikSelect name="role" label={roleLabel}>
          {ROLES_IN_ORDER.map((role) => (
            <Option key={role} value={role}>
              {roles[role]}
            </Option>
          ))}
        </FormikSelect>
        <RoleDefaults />
        <RoleFields />
        <span className="text-xs text-gray-500">
          <T _str="Orders and sales already attributed keep the role, commission or discount they had. A new role also updates the linked customers. They become managed by and billed to this organisation where the role says so, and go back to Pythagoras where it no longer does. Acquired by stays." />
        </span>
        <div className="mt-2">
          <Button variant="primary" size="sm" type="submit" loading={loading}>
            <T _str="save" swc />
          </Button>
        </div>
      </Form>
    </Formik>
  );
};
export const ReferralSettingsPanel = memo(ReferralSettingsPanel_);
